import * as Yup from 'yup'
import styled from '@emotion/styled'
import { yupResolver } from '@hookform/resolvers/yup'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'next-i18next'

import useOrder from 'modules/order/state/order/use-order'
import OrderDedicationOptions from 'modules/order/components/dedication/order-dedication-options'
import OrderDedicationHonoreeFields from 'modules/order/components/dedication/order-dedication-honoree-fields'
import { EMAIL_VALIDATION_REGEX } from 'utils/constants/misc'
import DrawerPanel from 'modules/drawer/components/drawer-panel/drawer-panel'
import useDrawer from 'modules/drawer/state/use-drawer'
import useConfig from 'modules/config/state/use-config'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import { focusOnElement } from 'modules/drawer/components/drawer/drawer.utils'
import { ORDER_DEDICATION_DRAWER_OPENER_DATA_ID } from './order-dedication-drawer-opener'

export interface DedicationFormData {
  type: 'honor' | 'memory'
  honoreeFirstName: string
  honoreeLastName: string
  isEmailNotification: boolean
  recipientFirstName: string
  recipientLastName: string
  recipientEmailAddress: string
  ecardId?: number
  ecardMessage: string
  isPrintCard: boolean
  recipientAddress: string
  recipientCity: string
  recipientState: string
  recipientPostalCode: string
  recipientCountry: string
}

const OrderDedicationDrawerPanel = () => {
  const config = useConfig()
  const drawer = useDrawer({ onClose: focusOnElement(ORDER_DEDICATION_DRAWER_OPENER_DATA_ID) })
  const order = useOrder()
  const { t } = useTranslation()

  const dedication = order.dedication.value
  const isContentPanelNull = config.contentPanel == null

  const isEmailToggled =
    dedication.recipientEmailAddress &&
    dedication.recipientFirstName &&
    dedication.recipientLastName
      ? true
      : false

  const isPrintCardToggled =
    dedication.recipientCountry && dedication.recipientAddress && dedication.recipientCity
      ? true
      : false

  const defaultValues: DedicationFormData = {
    isEmailNotification: isEmailToggled,
    isPrintCard: isPrintCardToggled,
    type: dedication.type || 'honor',
    honoreeFirstName: dedication.honoreeFirstName || '',
    honoreeLastName: dedication.honoreeLastName || '',
    recipientEmailAddress: dedication.recipientEmailAddress || '',
    recipientFirstName: dedication.recipientFirstName || '',
    recipientLastName: dedication.recipientLastName || '',
    ecardId: dedication.ecardId || undefined,
    ecardMessage: dedication.ecardMessage || '',
    recipientCountry: dedication.recipientCountry || 'US',
    recipientAddress: dedication ? dedication.recipientAddress : '',
    recipientCity: dedication.recipientCity || '',
    recipientState: dedication.recipientState || '',
    recipientPostalCode: dedication.recipientPostalCode || '',
  }

  const validationSchema = Yup.object().shape({
    isEmailNotification: Yup.boolean(),
    isPrintCard: Yup.boolean(),
    type: Yup.string().required(
      t('orderDedicationDrawerPanel.validation.type.required', 'Dedication type is required'),
    ),
    honoreeFirstName: Yup.string().required(
      t(
        'orderDedicationDrawerPanel.validation.honoreeFirstName.required',
        "Honoree's first name is required",
      ),
    ),
    honoreeLastName: Yup.string().required(
      t(
        'orderDedicationDrawerPanel.validation.honoreeLastName.required',
        "Honoree's last name is required",
      ),
    ),
    recipientFirstName: Yup.string().when('isEmailNotification', {
      is: true,
      then: (schema) =>
        schema.required(
          t(
            'orderDedicationDrawerPanel.validation.recipientFirstName.required',
            'Recipient first name is required',
          ),
        ),
    }),
    recipientLastName: Yup.string().when('isEmailNotification', {
      is: true,
      then: (schema) =>
        schema.required(
          t(
            'orderDedicationDrawerPanel.validation.recipientLastName.required',
            'Recipient last name is required',
          ),
        ),
    }),
    recipientEmailAddress: Yup.string().when('isEmailNotification', {
      is: true,
      then: (schema) =>
        schema
          .required(
            t(
              'orderDedicationDrawerPanel.validation.recipientEmailAddress.required',
              'Recipient email is required',
            ),
          )
          .matches(
            EMAIL_VALIDATION_REGEX,
            t(
              'orderDedicationDrawerPanel.validation.recipientEmailAddress.invalidFormat',
              'Please enter in the format: name@domain.com.',
            ),
          ),
    }),
    recipientCountry: Yup.string().when('isPrintCard', {
      is: true,
      then: (schema) =>
        schema.required(
          t(
            'orderDedicationDrawerPanel.validation.recipientCountry.required',
            'Country is required',
          ),
        ),
    }),
    recipientAddress: Yup.string().when('isPrintCard', {
      is: true,
      then: (schema) =>
        schema.required(
          t(
            'orderDedicationDrawerPanel.validation.recipientAddress.required',
            'Address is required',
          ),
        ),
    }),
    recipientCity: Yup.string().when('isPrintCard', {
      is: true,
      then: (schema) =>
        schema.required(
          t('orderDedicationDrawerPanel.validation.recipientCity.required', 'City is required'),
        ),
    }),
    recipientState: Yup.string().when('isPrintCard', {
      is: true,
      then: (schema) =>
        schema.required(
          t('orderDedicationDrawerPanel.validation.recipientState.required', 'State is required'),
        ),
    }),
    recipientPostalCode: Yup.string().when('isPrintCard', {
      is: true,
      then: (schema) =>
        schema
          .required(
            t(
              'orderDedicationDrawerPanel.validation.recipientPostalCode.required',
              'Zip code is required',
            ),
          )
          .matches(
            /^[0-9]{5}(?:-[0-9]{4})?$/,
            t(
              'orderDedicationDrawerPanel.validation.recipientPostalCode.invalid',
              'Invalid zip code',
            ),
          ),
    }),
  })

  const methods = useForm<DedicationFormData>({
    mode: 'onBlur',
    shouldFocusError: true,
    reValidateMode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues,
  })

  const {
    handleSubmit,
    setFocus,
    watch,
    setValue,
    unregister,
    formState: { errors },
  } = methods

  const onFormValid = (data: DedicationFormData) => {
    const isNotification = data.isEmailNotification || data.isPrintCard

    const recipientInfo = {
      recipientFirstName: isNotification ? data.recipientFirstName : '',
      recipientLastName: isNotification ? data.recipientLastName : '',
      recipientName: isNotification ? `${data.recipientFirstName} ${data.recipientLastName}` : '',
      ecardMessage: isNotification ? data.ecardMessage : '',
      ecardId: isNotification ? data.ecardId : undefined,
      recipientEmailAddress: data.isEmailNotification ? data.recipientEmailAddress : '',
      recipientAddress: data.isPrintCard ? data.recipientAddress : '',
      recipientCity: data.isPrintCard ? data.recipientCity : '',
      recipientState: data.isPrintCard ? data.recipientState : '',
      recipientPostalCode: data.isPrintCard ? data.recipientPostalCode : '',
      recipientCountry: data.isPrintCard ? data.recipientCountry : '',
    }

    order.dedication.setValue({
      ...recipientInfo,
      type: data.type,
      honoreeFirstName: data.honoreeFirstName,
      honoreeLastName: data.honoreeLastName,
      honoreeName: `${data.honoreeFirstName} ${data.honoreeLastName}`,
      hasDedication: true,
      isGiftAmountMessageIncluded: false,
    })

    drawer.close()
  }

  const onFormInvalid = async (errors: FieldErrors) => {
    setFocus(Object.keys(errors)[0] as keyof DedicationFormData)
  }

  const ecardInfo = {
    id: dedication.ecardId || undefined,
    src: config.dedication.eCards.find((ecard) => ecard.id === dedication.ecardId)?.image_url ?? '',
  }

  const onSave = handleSubmit(onFormValid, onFormInvalid)

  if (!drawer.current) {
    return null
  }

  return (
    <FormProvider {...methods}>
      <form>
        <DrawerPanel
          title={drawer.current.label}
          closeButtonDataId="dedication-drawer-close-button"
        >
          <DrawerPanel.Body>
            <OrderDedicationHonoreeFields
              type={watch('type')}
              errors={errors}
              setValue={setValue}
            />

            <OrderDedicationOptions
              errors={errors}
              ecardInfo={ecardInfo}
              cardList={config.dedication.eCards}
              isECardEnabled={config.dedication.isECardEnabled}
              isEmailToggled={watch('isEmailNotification')}
              isEmailEnabled={config.dedication.isEmailEnabled}
              isPrintedCardEnabled={config.dedication.isPrintedCardEnabled}
              isPrintCardToggled={isPrintCardToggled}
              unregister={unregister}
              setValue={setValue}
              watch={watch}
            />
          </DrawerPanel.Body>
          <DrawerPanel.Footer isMobileView={isContentPanelNull}>
            <DrawerPanel.FooterAction
              onClick={drawer.close}
              data-id="dedication-drawer-cancel-button"
              isMobileView={isContentPanelNull}
            >
              {t('orderDedicationDrawerPanel.footer.cancel', 'Cancel')}
            </DrawerPanel.FooterAction>
            <DrawerPanel.FooterAction
              onClick={onSave}
              data-id="dedication-drawer-save-button"
              color={DEFAULT_THEME_COLORS['--theme-color--neutral-20']}
              isMobileView={isContentPanelNull}
            >
              {t('orderDedicationDrawerPanel.footer.save', 'Save')}
            </DrawerPanel.FooterAction>
          </DrawerPanel.Footer>
        </DrawerPanel>
      </form>
    </FormProvider>
  )
}

export default OrderDedicationDrawerPanel
